import { NextSeo, type NextSeoProps } from 'next-seo';
import { memo, useMemo } from 'react';

import { useGetNotificationSummary } from '~/components/AirNotifications/hooks/useGetNotificationSummary';
import { I18n } from '~/constants/I18n';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

const PRIVATE_PAGE_SEO_TITLE_MAX_NOTIFICATIONS = 99;

export type PrivateSeoPageTitleProps = NextSeoProps;

export const PrivatePageSeoTitle = memo(({ title, ...restOfProps }: PrivateSeoPageTitleProps) => {
  const { data: notificationsSummary } = useGetNotificationSummary();
  const { currentWorkspace } = useCurrentWorkspace();

  const formattedTitle = useMemo(() => {
    const unread = notificationsSummary?.unseen || 0;
    const countToDisplay =
      unread > PRIVATE_PAGE_SEO_TITLE_MAX_NOTIFICATIONS ? `+${PRIVATE_PAGE_SEO_TITLE_MAX_NOTIFICATIONS}` : unread;
    const titleWithFallback = title ?? currentWorkspace?.name;

    if (unread > 0 && titleWithFallback) {
      return `(${countToDisplay}) ${titleWithFallback}`;
    }

    return titleWithFallback;
  }, [currentWorkspace?.name, notificationsSummary?.unseen, title]);

  return <NextSeo title={formattedTitle} defaultTitle={I18n.basePageTitle} {...restOfProps} />;
});

PrivatePageSeoTitle.displayName = 'PrivatePageSeoTitle';
